import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import AuthShell from '../components/auth/AuthShell';
import InvitePreview from '../components/auth/InvitePreview';
import PasswordStrength, { evaluatePassword } from '../components/auth/PasswordStrength';

const API = `${window.location.origin}/api`;

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [emailLocked, setEmailLocked] = useState(false);
  const [inviteValid, setInviteValid] = useState(false);

  const handleInviteResolved = useCallback((data) => {
    if (!data || !data.invite || data.invite.status !== 'pending') {
      setInviteValid(false);
      return;
    }
    setEmail(data.invite.email);
    setEmailLocked(true);
    setInviteValid(true);
  }, []);

  const pwResult = evaluatePassword(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!terms) {
      toast.error('Şartları kabul etmeniz gerekiyor');
      return;
    }
    if (!pwResult.valid) {
      toast.error('Şifre politikasını karşılamıyor');
      return;
    }

    setSubmitting(true);
    try {
      const body = { name, email, password };
      if (inviteToken && inviteValid) body.inviteToken = inviteToken;

      const res = await axios.post(`${API}/auth/register`, body);
      const inviteCtx = res.data?.inviteContext || null;
      navigate('/check-email', {
        replace: true,
        state: { email, inviteContext: inviteCtx },
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.error || 'Kayıt başarısız');
      toast.error(msg);
      setSubmitting(false);
    }
  };

  const banner = inviteToken ? (
    <InvitePreview token={inviteToken} onResolved={handleInviteResolved} />
  ) : null;

  return (
    <AuthShell banner={banner}>
      <div className="ll-hero" style={{ marginBottom: 24 }}>
        <h1>Hesap oluştur.</h1>
        <p className="ll-sub">
          {inviteToken
            ? 'Davete kayıt olarak workspace\'e katılabilirsin.'
            : 'Ücretsiz başla, kendi workspace\'ini oluştur.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} autoComplete="on" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div className="ll-label"><span>Ad Soyad</span></div>
          <input
            className="ll-field-input"
            type="text"
            name="name"
            placeholder="Ad Soyad"
            autoComplete="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="register-name-input"
          />
        </div>

        <div>
          <div className="ll-label"><span>E-posta</span></div>
          <input
            className="ll-field-input"
            type="email"
            name="email"
            placeholder="ad.soyad@onxflow.co"
            autoComplete="email"
            required
            disabled={emailLocked}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={emailLocked ? { opacity: .65, cursor: 'not-allowed' } : undefined}
            data-testid="register-email-input"
          />
          {emailLocked && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Davet edilen e-posta ile devam edilecek
            </div>
          )}
        </div>

        <div>
          <div className="ll-label"><span>Şifre</span></div>
          <div className="ll-password-wrap">
            <input
              className="ll-field-input"
              type={showPw ? 'text' : 'password'}
              name="password"
              placeholder="••••••••••"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ paddingRight: 42 }}
              data-testid="register-password-input"
            />
            <button
              type="button"
              className="ll-toggle-eye"
              aria-label={showPw ? 'Şifreyi gizle' : 'Şifreyi göster'}
              onClick={() => setShowPw((v) => !v)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          </div>
          <PasswordStrength password={password} />
        </div>

        <label
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 4, userSelect: 'none' }}
          onClick={(e) => { e.preventDefault(); setTerms((v) => !v); }}
        >
          <span className="ll-check" data-on={terms ? 'true' : 'false'}>
            {terms && (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12l4.5 4.5L19 7" />
              </svg>
            )}
          </span>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            <a href="#sartlar" onClick={(e) => e.preventDefault()} style={{ color: 'var(--accent)' }}>Kullanım şartları</a>
            {' '}ve{' '}
            <a href="#gizlilik" onClick={(e) => e.preventDefault()} style={{ color: 'var(--accent)' }}>gizlilik politikası</a>
            'nı kabul ediyorum
          </span>
        </label>

        <button
          className="ll-primary"
          type="submit"
          style={{ marginTop: 8 }}
          disabled={submitting}
          data-testid="register-submit-button"
        >
          <span>{submitting ? 'Kayıt yapılıyor…' : 'Kayıt ol'}</span>
          {!submitting && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14" />
              <path d="M13 6l6 6-6 6" />
            </svg>
          )}
        </button>

        <div className="ll-divider mono" style={{ marginTop: 8 }}>VEYA</div>

        <Link
          to={inviteToken ? `/?invite=${encodeURIComponent(inviteToken)}` : '/'}
          className="ll-ghost"
          style={{ textDecoration: 'none' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" />
            <path d="M10 17l-5-5 5-5" />
            <path d="M15 12H5" />
          </svg>
          <span>Zaten hesabım var, giriş yap</span>
        </Link>
      </form>
    </AuthShell>
  );
}
